/**
 * CompetencyScoreTable Component
 *
 * Displays dimension-level scoring breakdown with expandable evidence.
 * Reused pattern from Scores.tsx with collapsible evidence support.
 */

import { useState } from 'react';
import { EvidenceSnippet } from './EvidenceSnippet';
import type { DimensionScore } from '../lib/types';

interface CompetencyScoreTableProps {
  /** Dimension scores from scoring result */
  dimensionScores: DimensionScore[];
  /** Show confidence column */
  showConfidence?: boolean;
}

export function CompetencyScoreTable({
  dimensionScores,
  showConfidence = true,
}: CompetencyScoreTableProps) {
  const [expandedDimension, setExpandedDimension] = useState<string | null>(null);

  if (dimensionScores.length === 0) {
    return <p className="text-muted">No dimension scores available.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {dimensionScores.map((dim) => {
        const isExpanded = expandedDimension === dim.dimensionId;
        const percentage = dim.maxScore > 0 ? Math.round((dim.score / dim.maxScore) * 100) : 0;

        return (
          <div
            key={dim.dimensionId}
            style={{
              padding: 12,
              background: 'var(--bg-muted, #f9fafb)',
              borderRadius: 6,
              border: '1px solid var(--border-default, #e5e7eb)',
            }}
          >
            {/* Header Row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: dim.evidence.length > 0 ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (dim.evidence.length > 0) {
                  setExpandedDimension(isExpanded ? null : dim.dimensionId);
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Expand/collapse indicator */}
                {dim.evidence.length > 0 && (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--text-tertiary)',
                      transition: 'transform 0.2s',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    ▶
                  </span>
                )}

                {/* Dimension name */}
                <strong style={{ fontSize: 14 }}>{dim.dimensionId}</strong>

                {/* Weight badge */}
                <span
                  style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    background: 'var(--bg-secondary, #e5e7eb)',
                    borderRadius: 4,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {(dim.weight * 100).toFixed(0)}% weight
                </span>
              </div>

              {/* Score display */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Confidence if available */}
                {showConfidence && dim.confidence !== undefined && (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {(dim.confidence * 100).toFixed(0)}% conf
                  </span>
                )}

                {/* Score bar */}
                <div
                  style={{
                    width: 80,
                    height: 6,
                    background: 'var(--bg-secondary, #e5e7eb)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${percentage}%`,
                      height: '100%',
                      background: percentage >= 70 ? 'var(--semantic-success, #22c55e)' : percentage >= 40 ? 'var(--semantic-warning, #f59e0b)' : 'var(--semantic-error, #ef4444)',
                      borderRadius: 3,
                    }}
                  />
                </div>

                {/* Score text */}
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    fontFeatureSettings: '"tnum"',
                    minWidth: 90,
                    textAlign: 'right',
                  }}
                >
                  {dim.score.toFixed(2)} / {dim.maxScore.toFixed(2)}
                </span>

                {/* Weighted score */}
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    minWidth: 70,
                    textAlign: 'right',
                  }}
                >
                  (wt: {dim.weightedScore.toFixed(2)})
                </span>
              </div>
            </div>

            {/* Evidence section (collapsible) */}
            {isExpanded && dim.evidence.length > 0 && (
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border-default, #e5e7eb)',
                }}
              >
                <EvidenceSnippet evidence={dim.evidence} maxVisible={5} />
              </div>
            )}

            {/* Evidence count hint when collapsed */}
            {!isExpanded && dim.evidence.length > 0 && (
              <div
                className="text-muted text-small"
                style={{ marginTop: 4, fontSize: 11 }}
              >
                {dim.evidence.length} evidence span{dim.evidence.length !== 1 ? 's' : ''} — click to expand
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
